import { Injectable, signal } from '@angular/core';

/**
 * Centralizes the alert-animation toggles that affect performance on weak
 * computers. Three independent flags can each disable the pulse animations
 * on critical/moderate workbench counters (TASK-58394):
 *
 *  - User preference: persisted in localStorage and toggled in Settings.
 *    The user explicitly chose "Reduzir animações de alerta".
 *
 *  - Low-perf auto-detection: set once on app start when
 *    `navigator.hardwareConcurrency` or `navigator.deviceMemory` indicate
 *    a low-end device. Acts as a safe default.
 *
 *  - Tab hidden: pauses the animations while the tab is in the background.
 *    Modern browsers throttle background tabs, but doing it explicitly
 *    guarantees the behavior across browsers and avoids the burst of
 *    repaint when the tab regains focus.
 *
 * Each flag adds a CSS class to `<body>` (`reduced-motion`, `low-perf`,
 * `is-tab-hidden`); the SCSS in workbench-results-viewer maps each class to
 * the same outcome — animation paused or removed, halo kept visible.
 */
@Injectable({ providedIn: 'root' })
export class AnimationPreferenceService {
  private readonly STORAGE_KEY = 'autolac.alert.reducedMotion';

  /**
   * Effective user toggle state — what the button in the page header reflects.
   * On a low-perf machine where the user hasn't decided yet, this comes up
   * already `true` so the visible state matches the actual behavior. Once the
   * user clicks the toggle even once, their choice sticks (we save explicitly
   * to localStorage and stop letting autodetect override it).
   */
  readonly userPreferenceEnabled = signal(false);

  /** Snapshot of the autodetect heuristic (informational, used at init). */
  readonly lowPerfDetected = signal(false);

  /** Wires up DOM classes and listeners. Called once from AppComponent. */
  initialize(): void {
    const stored = this.readStoredPreference();
    const isLowPerf = this.detectLowPerf();
    this.lowPerfDetected.set(isLowPerf);
    document.body.classList.toggle('low-perf', isLowPerf);

    // First-run on a low-perf device: pre-enable the user toggle so the
    // header button matches what the user already sees on screen (animation
    // off). We don't write to localStorage yet — that only happens once the
    // user explicitly clicks the toggle, which then becomes their saved
    // preference for any device they sign in from.
    const initialUserPreference = stored ?? isLowPerf;
    this.userPreferenceEnabled.set(initialUserPreference);
    this.applyUserPreferenceClass(initialUserPreference);

    this.wireVisibilityListener();
  }

  /** Toggles the user preference and persists it. */
  toggleUserPreference(): void {
    const next = !this.userPreferenceEnabled();
    this.userPreferenceEnabled.set(next);
    try {
      localStorage.setItem(this.STORAGE_KEY, next ? '1' : '0');
    } catch {
      // localStorage may be unavailable in private mode — preference becomes
      // session-only, which is acceptable.
    }
    this.applyUserPreferenceClass(next);
  }

  // ── Internals ──────────────────────────────────────────────────────────

  /**
   * Returns the explicitly saved preference, or `null` if the user has never
   * toggled. The null signal is what lets autodetect take over on first run
   * without overwriting a real user choice.
   */
  private readStoredPreference(): boolean | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw === '1') return true;
      if (raw === '0') return false;
      return null;
    } catch {
      return null;
    }
  }

  private applyUserPreferenceClass(enabled: boolean): void {
    document.body.classList.toggle('reduced-motion', enabled);
  }

  private detectLowPerf(): boolean {
    // Heuristics: ≤4 logical cores OR ≤2 GiB device memory is a strong
    // signal of an entry-level office PC where box-shadow / filter blur
    // animations stutter. We don't FPS-sample because that's noisy and
    // the heuristic above already covers the QA-reported scenario.
    const cores = navigator.hardwareConcurrency ?? 8;
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
    return cores <= 4 || deviceMemory <= 2;
  }

  private wireVisibilityListener(): void {
    const apply = () => document.body.classList.toggle('is-tab-hidden', document.hidden);
    apply();
    document.addEventListener('visibilitychange', apply);
  }
}
